import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AIRecommendations = ({ recommendations }) => {
  const [expandedCards, setExpandedCards] = useState({});

  const toggleExpand = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev?.[index]
    }));
  };

  const getPriorityColor = (priority) => {
    const colorMap = {
      'High': 'var(--color-error)',
      'Medium': 'var(--color-warning)',
      'Low': 'var(--color-accent)'
    };
    return colorMap?.[priority] || 'var(--color-muted)';
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'Behavioral Safety': 'Users',
      'Permit Management': 'FileText',
      'Incident Prevention': 'Shield',
      'Audit Compliance': 'CheckCircle',
      'Risk Mitigation': 'AlertTriangle',
      'Training': 'BookOpen'
    };
    return iconMap?.[category] || 'Lightbulb';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Sparkles" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">AI-Powered Recommendations</h3>
          <p className="text-xs text-muted-foreground">
            Data-driven insights based on your safety performance metrics
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {recommendations?.map((rec, index) => (
          <div 
            key={index}
            className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-all duration-200"
          >
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${getPriorityColor(rec?.priority)}20` }}
                  >
                    <Icon 
                      name={getCategoryIcon(rec?.category)} 
                      size={20} 
                      color={getPriorityColor(rec?.priority)} 
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-foreground">{rec?.title}</h4>
                      <span 
                        className="px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{ 
                          backgroundColor: `${getPriorityColor(rec?.priority)}20`,
                          color: getPriorityColor(rec?.priority)
                        }}
                      >
                        {rec?.priority} Priority
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{rec?.summary}</p>
                    <div className="flex items-center gap-3 text-xs">
                      <div className="flex items-center gap-1">
                        <Icon name="Tag" size={12} color="var(--color-muted-foreground)" />
                        <span className="text-muted-foreground">{rec?.category}</span>
                      </div>
                      {rec?.expectedImpact && (
                        <div className="flex items-center gap-1">
                          <Icon name="TrendingUp" size={12} color="var(--color-success)" />
                          <span className="text-success">{rec?.expectedImpact}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName={expandedCards?.[index] ? 'ChevronUp' : 'ChevronDown'}
                  onClick={() => toggleExpand(index)}
                  className="ml-2"
                />
              </div>

              {expandedCards?.[index] && (
                <div className="mt-4 pt-4 border-t border-border space-y-4">
                  {rec?.details && (
                    <div>
                      <p className="text-xs font-medium text-foreground mb-2">Detailed Analysis:</p>
                      <p className="text-xs text-muted-foreground">{rec?.details}</p>
                    </div>
                  )}

                  {rec?.actionSteps && rec?.actionSteps?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-foreground mb-2">Recommended Actions:</p>
                      <div className="space-y-2">
                        {rec?.actionSteps?.map((step, stepIndex) => (
                          <div key={stepIndex} className="flex items-start gap-2 bg-muted/30 rounded-lg p-2">
                            <div className="w-5 h-5 bg-accent/20 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-xs font-medium text-accent">{stepIndex + 1}</span>
                            </div>
                            <span className="text-xs text-foreground">{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {rec?.basedOn && rec?.basedOn?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-foreground mb-2">Based On:</p>
                      <div className="flex flex-wrap gap-2">
                        {rec?.basedOn?.map((metric, metricIndex) => (
                          <span 
                            key={metricIndex}
                            className="px-2 py-1 bg-muted/50 rounded text-xs text-muted-foreground"
                          >
                            {metric}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Check"
                      onClick={() => console.log('Implement recommendation:', rec?.title)}
                    >
                      Implement
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="BookmarkPlus"
                      onClick={() => console.log('Save recommendation:', rec?.title)}
                    >
                      Save for Later
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AIRecommendations;